import { Expand, LoaderCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PreviewDocument } from "@/features/credentials/hooks/use-credential-document-url";
import { useCredentialEvidencePreview } from "@/features/credentials/hooks/use-credential-evidence-preview";
import type { CredentialVaultScope } from "../../lib/credential-vault-scope";
import { CredentialDocumentThumbnail } from "../credential-card/credential-document-thumbnail";

export function ProviderEvidencePreview({
  scope,
  document,
  isReviewDocument,
  onExpand,
}: {
  scope: CredentialVaultScope;
  document: PreviewDocument;
  isReviewDocument: boolean;
  onExpand?: () => void;
}) {
  const preview = useCredentialEvidencePreview({
    scope,
    documentId: document.id,
    isReviewDocument,
  });
  const title = document.title || document.fileName;
  let content;
  if (preview.isLoading) {
    content = (
      <span
        role="status"
        className="flex h-full items-center justify-center gap-2 text-xs text-slate-500"
      >
        <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
        Loading document preview…
      </span>
    );
  } else if (preview.error) {
    content = (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 p-5 text-center text-sm text-slate-600"
      >
        <p>{preview.error}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={preview.retry}
        >
          <RefreshCw className="mr-2 size-3.5" aria-hidden="true" />
          Retry preview
        </Button>
      </div>
    );
  } else if (preview.url) {
    const thumbnail = (
      <CredentialDocumentThumbnail
        url={preview.url}
        fileName={document.fileName}
        title={title}
      />
    );
    content = onExpand ? (
      <button
        type="button"
        className="block h-full w-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        aria-label={`Expand preview of ${title}`}
        onClick={onExpand}
      >
        {thumbnail}
      </button>
    ) : (
      thumbnail
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
      <div className="h-56 sm:h-64">{content}</div>
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-3 py-2">
        <p
          className="min-w-0 truncate text-xs text-slate-500"
          title={document.fileName}
        >
          {document.fileName}
        </p>
        {onExpand && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 gap-1.5 rounded-lg px-2 text-xs font-semibold text-primary"
            onClick={onExpand}
          >
            <Expand className="size-3.5" aria-hidden="true" />
            Expand preview
          </Button>
        )}
      </div>
    </div>
  );
}
